<script lang="ts">
	//Helpers
	import {
		getWebHookEvents,
		getWebHookTypes,
	} from 'src/helpers/actions/webhooks/core/webhook_helpers';
	//Formatting
	import { format } from 'svelte-i18n';
	//Design system components
	import { Select, Text, type SelectOptionPropsType } from '@Shelly/shelly-design-system';
	//Components
	import WebhookConditions from './WebhookConditions.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type {
		WebHookEventOption,
		WebHooksParams,
		WebHookSupportedList,
	} from 'src/types/requests/settings/webhooks';

	export let device: Device;
	export let actionProps: WebHooksParams;
	export let supportedActionsList: WebHookSupportedList;
	export let eventsIndex = -1;
	export let componentIndex = 0;
	export let eventsOptions: WebHookEventOption[] = [];
	export let components: WebHooksParams['components'] = [];
	export let error: { show: boolean; label: string };

	const componentOptions: SelectOptionPropsType[] = components.map((component, index) => ({
		value: index,
		title: component.title,
	}));

	const baseActionProps = {
		...getWebHookTypes(device),
		id: actionProps.id,
		enable: actionProps.enable,
		name: actionProps.name,
		event: actionProps.event,
		active_between: actionProps.active_between,
		condition: actionProps.condition,
		repeat_period: actionProps.repeat_period,
		set_active_time: actionProps.set_active_time,
		urls: actionProps.urls,
	};

	const getLnmComponentIndex = (): number => {
		const index = components?.findIndex(
			(item) =>
				item.cid === actionProps.cid &&
				(actionProps.event?.startsWith(`${item.type}.`) || item.type === 'lnm'),
		);
		return index >= 0 ? index : 0;
	};

	const selectLnmComponent = (index: number) => {
		if (!components?.[index]) return;
		componentIndex = index;
		actionProps = { ...actionProps, ...components[index] };
	};

	const changeLnmComponent = (index: number) => {
		selectLnmComponent(index);
		updateEventsOptions();
	};

	const selectBaseComponent = () => {
		actionProps = { ...actionProps, ...baseActionProps };
	};

	const updateEventsOptions = () => {
		const events = getWebHookEvents(
			{ ...baseActionProps, event: actionProps.event, condition: actionProps.condition },
			supportedActionsList,
			device,
		);
		eventsOptions = events.options;
		eventsIndex = eventsOptions.length === 1 ? 0 : events.index;
	};

	const changeEvent = (e: CustomEvent) => {
		const condition = e.detail.event === actionProps.event ? actionProps.condition : '';
		if (e.detail.event?.startsWith('lnm.')) {
			selectLnmComponent(getLnmComponentIndex());
		} else {
			selectBaseComponent();
		}
		actionProps = { ...actionProps, event: e.detail.event, condition };
		updateEventsOptions();
	};

	if (actionProps.event?.startsWith('lnm.')) {
		selectLnmComponent(getLnmComponentIndex());
	}
	updateEventsOptions();
</script>

<WebhookConditions bind:actionProps bind:eventsOptions bind:eventsIndex {error} on:change={changeEvent}>
	<div slot="lnm-component-selector">
		{#if componentOptions.length > 1}
			<Text element="h2" class="mb-s">
				{$format('select_target_component', { default: 'Select target component:' })}
			</Text>
			<Select
				size="m"
				value={componentOptions[componentIndex]}
				options={componentOptions}
				icon="fal fa-microchip"
				on:change={(event) => changeLnmComponent(Number(event.detail['value']))}
				id="select-lnm-webhook-component"
				aria-label={$format('select_target_component', {
					default: 'Select target component',
				})}
			/>
		{/if}
	</div>
</WebhookConditions>
